// pages/customers/CustomerSearchBar.tsx
import { Search, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { EmployeeTreeFilter } from '@/components/employee-tree-filter'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { CustomerFilter } from '@/types/customer'
import type { CustomerCategory } from '@/types/customer-category'
import { CUSTOMER_CATEGORY_TYPES, VIP_LEVEL_OPTIONS } from '@/types/customer-category'

interface CustomerSearchBarProps {
  value: CustomerFilter & { date_range?: [string, string] }
  onChange: (value: CustomerSearchBarProps['value']) => void
  onSearch: () => void
  onReset: () => void
  categoryOptions?: CustomerCategory[]
  /** 是否展示「无归属客户」筛选（超管专属） */
  enableUnassigned?: boolean
}

export function CustomerSearchBar({ value, onChange, onSearch, onReset, categoryOptions = [], enableUnassigned = false }: CustomerSearchBarProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">录入人员</label>
          <DepartmentScopeFilter
            value={{ scope: value.scope || 'my', department_id: value.department_id }}
            enableUnassigned={enableUnassigned}
            onChange={(scopeValue) =>
              onChange({
                ...value,
                department_id: undefined,
                ...scopeValue,
              })
            }
          />
        </div>

        <div className="w-[200px]">
          <label htmlFor="customer-search-name" className="text-sm font-medium mb-1.5 block">客户名</label>
          <Input
            id="customer-search-name"
            placeholder="输入客户名"
            value={value.name || ''}
            onChange={e => onChange({ ...value, name: e.target.value || undefined })}
          />
        </div>

        <div className="w-[200px]">
          <label htmlFor="customer-search-phone" className="text-sm font-medium mb-1.5 block">手机号</label>
          <Input
            id="customer-search-phone"
            placeholder="输入手机号"
            value={value.phone || ''}
            onChange={e => onChange({ ...value, phone: e.target.value || undefined })}
          />
        </div>

        <div className="w-[200px]">
          <label htmlFor="customer-search-wechat" className="text-sm font-medium mb-1.5 block">微信号</label>
          <Input
            id="customer-search-wechat"
            placeholder="输入微信号"
            value={value.wechat || ''}
            onChange={e => onChange({ ...value, wechat: e.target.value || undefined })}
          />
        </div>

        <div className="w-[200px]">
          <label htmlFor="customer-category-filter" className="text-sm font-medium mb-1.5 block">客户类型</label>
          <select
            id="customer-category-filter"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={value.category_id ?? ''}
            onChange={(event) => {
              const nextCategoryId = event.target.value ? Number(event.target.value) : undefined
              const nextCategory = categoryOptions.find(category => category.id === nextCategoryId)
              onChange({
                ...value,
                category_id: nextCategoryId,
                vip_level: nextCategory && nextCategory.type !== 0 ? value.vip_level : undefined,
              })
            }}
          >
            <option value="">全部类型</option>
            {categoryOptions.map(category => {
              const typeLabel = CUSTOMER_CATEGORY_TYPES.find(type => type.value === category.type)?.label
              return (
                <option key={category.id} value={category.id}>
                  {category.name}{typeLabel ? `（${typeLabel}）` : ''}
                </option>
              )
            })}
          </select>
        </div>

        <div className="w-[160px]">
          <label htmlFor="customer-vip-filter" className="text-sm font-medium mb-1.5 block">VIP等级</label>
          <select
            id="customer-vip-filter"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={value.vip_level ?? ''}
            onChange={(event) => onChange({
              ...value,
              vip_level: event.target.value ? Number(event.target.value) : undefined,
            })}
          >
            <option value="">全部VIP</option>
            {VIP_LEVEL_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-[260px]">
          <EmployeeTreeFilter
            label="锁定人"
            placeholder="选择锁定人"
            value={value.user_ids}
            onChange={(userIds) => onChange({ ...value, user_ids: userIds.length > 0 ? userIds : undefined })}
          />
        </div>

        <div className="w-[260px]">
          <EmployeeTreeFilter
            label="跟踪人"
            placeholder="选择跟踪人"
            value={value.tracker_ids}
            onChange={(trackerIds) => onChange({ ...value, tracker_ids: trackerIds.length > 0 ? trackerIds : undefined })}
          />
        </div>

        <div className="w-[140px]">
          <label htmlFor="customer-search-blacklist" className="text-sm font-medium mb-1.5 block">黑名单</label>
          <Select
            value={value.blacklist === undefined ? 'all' : String(value.blacklist)}
            onValueChange={(v) =>
              onChange({
                ...value,
                blacklist: v === 'all' ? undefined : v === 'true',
              })
            }
          >
            <SelectTrigger id="customer-search-blacklist">
              <SelectValue placeholder="黑名单" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="true">黑名单</SelectItem>
              <SelectItem value="false">非黑名单</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Button onClick={onSearch}>
            <Search className="mr-1.5 h-4 w-4" />
            查询
          </Button>
          <Button variant="outline" onClick={onReset}>
            <RotateCcw className="mr-1.5 h-4 w-4" />
            重置
          </Button>
        </div>
      </div>
    </div>
  )
}
